// src/lib/firebase.js
import { initializeApp } from 'firebase/app';
import { getMessaging, getToken, onMessage, isSupported } from 'firebase/messaging';

// Firebase configuration
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// VAPID key for FCM (should match the one in Firebase Console)
const VAPID_KEY = import.meta.env.VITE_FCM_VAPID_KEY;

let app = null;
let messaging = null;

// Initialize Firebase only if config is available
const initializeFirebase = async () => {
  try {
    // Check if all required config values are present
    if (!firebaseConfig.apiKey || !firebaseConfig.projectId) {
      console.warn('⚠️ Firebase configuration not found. FCM features will be disabled.');
      return null;
    }

    // Check if FCM is supported in this browser
    const supported = await isSupported();
    if (!supported) {
      console.warn('⚠️ Firebase Cloud Messaging is not supported in this browser.');
      return null;
    }

    app = initializeApp(firebaseConfig);
    messaging = getMessaging(app);
    return messaging;
  } catch (error) {
    console.error('❌ Firebase initialization failed:', error);
    return null;
  }
};

// Get FCM token
export const getFCMToken = async () => {
  try {
    if (!messaging) {
      messaging = await initializeFirebase();
      if (!messaging) return null;
    }

    if (!VAPID_KEY) {
      console.error('❌ FCM VAPID key not found in environment variables');
      return null;
    }

    // Check if service worker is registered
    const registration = await navigator.serviceWorker.getRegistration('/firebase-messaging-sw.js');
    if (!registration) {
      console.warn('⚠️ Firebase messaging service worker not found');
      return null;
    }

    const token = await getToken(messaging, { 
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration
    });

    if (token) {
      return token;
    } else {
      return null;
    }
  } catch (error) {
    console.error('❌ Failed to get FCM token:', error);
    return null;
  }
};

// Listen for foreground messages
export const onForegroundMessage = (callback) => {
  if (!messaging) return null;

  return onMessage(messaging, (payload) => {
    callback(payload);
  });
};

// Check if FCM is available and properly configured
export const isFCMAvailable = async () => {
  try {
    const supported = await isSupported();
    return supported && !!firebaseConfig.apiKey && !!VAPID_KEY;
  } catch (error) {
    return false;
  }
};

export { messaging };